/**
 * URL Type Detection Utility
 * Detects the type of URL for proper handling in the application
 */

// Magic domain for DXA proxy URLs (URLs that will be transformed to DXA)
const MAGIC_DOMAIN = 'ask-cara.com';

/**
 * Check if URL is a magic DXA proxy URL (ask-cara.com domain)
 * @param {string} url - URL to check
 * @returns {boolean} True if URL uses the magic domain
 */
export function isMagicDxaUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.toLowerCase().includes(MAGIC_DOMAIN);
  } catch {
    return false;
  }
}

/**
 * Detect the type of URL
 * @param {string} url - URL to analyze
 * @returns {string} URL type: 'dxa', 'dxa-proxy', 'pegawebembed', 'regular'
 */
export function detectUrlType(url) {
  if (!url || typeof url !== 'string') return 'unknown';

  const lowerUrl = url.toLowerCase();

  // Check for magic DXA proxy URL (ask-cara.com domain)
  // These URLs contain action/page parameters but NOT full DXA config
  if (isMagicDxaUrl(url)) {
    return 'dxa-proxy';
  }

  // Check for full DXA URL patterns
  // DXA URLs typically have these characteristics:
  // - pegaServerUrl parameter
  // - appAlias parameter
  // - clientId parameter
  // - action parameter
  // - May contain caseId, assignmentId, pageId, etc.
  if (
    (lowerUrl.includes('pegaserverurl=') || lowerUrl.includes('pega-server-url=')) &&
    (lowerUrl.includes('appalias=') || lowerUrl.includes('app-alias=')) &&
    lowerUrl.includes('clientid=') &&
    (lowerUrl.includes('action=') || lowerUrl.includes('pageid='))
  ) {
    return 'dxa';
  }

  // Check for PegaWebEmbed indicators (without action parameter)
  // PegaWebEmbed uses pega-embed component directly
  if (
    lowerUrl.includes('appalias=') &&
    lowerUrl.includes('clientid=') &&
    !lowerUrl.includes('action=')
  ) {
    return 'pegawebembed';
  }

  // Regular URL
  return 'regular';
}

/**
 * Parse magic DXA proxy URL parameters (ask-cara.com URLs)
 * Extracts DXA parameters from ask-cara.com URL
 * @param {string} url - Magic DXA proxy URL to parse
 * @returns {Object|null} Parsed parameters or null if invalid
 */
export function parseMagicDxaUrl(url) {
  if (!isMagicDxaUrl(url)) return null;

  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    return {
      action: params.get('action'),
      pageId: params.get('pageId') || params.get('page-id'),
      pageClass: params.get('pageClass') || params.get('page-class'),
      authFlow: params.get('authFlow') || params.get('auth-flow'),
      caseId: params.get('caseId') || params.get('case-id'),
      assignmentId: params.get('assignmentId') || params.get('assignment-id'),
      workId: params.get('workId') || params.get('work-id'),
      // Extract any additional parameters
      additionalParams: Object.fromEntries(
        Array.from(params.entries()).filter(
          ([key]) =>
            ![
              'action',
              'pageId',
              'page-id',
              'pageClass',
              'page-class',
              'authFlow',
              'auth-flow',
              'caseId',
              'case-id',
              'assignmentId',
              'assignment-id',
              'workId',
              'work-id'
            ].includes(key)
        )
      )
    };
  } catch (error) {
    console.error('Failed to parse magic DXA URL:', error);
    return null;
  }
}

/**
 * Parse DXA URL parameters
 * @param {string} url - DXA URL to parse
 * @returns {Object|null} Parsed parameters or null if invalid
 */
export function parseDxaUrl(url) {
  if (detectUrlType(url) !== 'dxa') return null;

  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    return {
      baseUrl: `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`,
      pegaServerUrl: params.get('pegaServerUrl') || params.get('pega-server-url'),
      appAlias: params.get('appAlias') || params.get('app-alias'),
      clientId: params.get('clientId') || params.get('client-id'),
      clientSecret: params.get('clientSecret') || params.get('client-secret'),
      action: params.get('action'),
      pageId: params.get('pageId') || params.get('page-id'),
      pageClass: params.get('pageClass') || params.get('page-class'),
      authFlow: params.get('authFlow') || params.get('auth-flow'),
      caseId: params.get('caseId') || params.get('case-id'),
      assignmentId: params.get('assignmentId') || params.get('assignment-id'),
      workId: params.get('workId') || params.get('work-id'),
      // Extract any additional parameters
      additionalParams: Object.fromEntries(
        Array.from(params.entries()).filter(
          ([key]) =>
            ![
              'pegaServerUrl',
              'pega-server-url',
              'appAlias',
              'app-alias',
              'clientId',
              'client-id',
              'clientSecret',
              'client-secret',
              'action',
              'pageId',
              'page-id',
              'pageClass',
              'page-class',
              'authFlow',
              'auth-flow',
              'caseId',
              'case-id',
              'assignmentId',
              'assignment-id',
              'workId',
              'work-id'
            ].includes(key)
        )
      )
    };
  } catch (error) {
    console.error('Failed to parse DXA URL:', error);
    return null;
  }
}

/**
 * Check if a URL should open in AppView
 * @param {string} url - URL to check
 * @returns {boolean} True if URL should open in AppView
 */
export function shouldOpenInAppView(url) {
  const urlType = detectUrlType(url);
  return urlType === 'dxa' || urlType === 'dxa-proxy' || urlType === 'pegawebembed';
}

/**
 * Transform magic DXA proxy URL to full DXA config using brand config
 * @param {string} url - Magic DXA proxy URL (ask-cara.com)
 * @param {Object} brandConfig - Brand configuration with DXA settings
 * @returns {Object|null} DXA config object or null if invalid
 */
export function transformMagicUrlToDxa(url, brandConfig) {
  if (!isMagicDxaUrl(url)) {
    console.warn('URL is not a magic DXA proxy URL:', url);
    return null;
  }

  if (!brandConfig) {
    console.error('❌ Brand config is missing');
    console.warn('💡 Make sure brand config is loaded in Redux state');
    return null;
  }

  // Check if DXA config exists in brand config
  if (!brandConfig.dxa) {
    console.error('❌ DXA config missing in brand config');
    console.warn('💡 Add "dxa" object to your brand config');
    return null;
  }

  const dxaConfig = brandConfig.dxa;
  
  // Check if DXA is enabled
  if (dxaConfig.enabled === false) {
    console.warn('⚠️ DXA is disabled in brand config');
    return null;
  }

  const magicParams = parseMagicDxaUrl(url);
  if (!magicParams) {
    console.error('Failed to parse magic DXA URL');
    return null;
  }

  // Two possible DXA config formats:
  // 1. Full format: pegaServerUrl, appAlias, clientId, clientSecret
  // 2. BaseUrl format: baseUrl (for hosted DXA apps)
  
  let transformedConfig;
  
  if (dxaConfig.baseUrl) {
    // BaseUrl format - for hosted DXA React apps
    console.log('🎯 Using baseUrl DXA format');
    transformedConfig = {
      baseUrl: dxaConfig.baseUrl,
      authFlow: magicParams.authFlow || dxaConfig.authFlow || dxaConfig.defaultAuthFlow,
      action: magicParams.action || dxaConfig.defaultAction,
      pageId: magicParams.pageId || dxaConfig.defaultPageId,
      pageClass: magicParams.pageClass || dxaConfig.defaultPageClass,
      caseId: magicParams.caseId,
      assignmentId: magicParams.assignmentId,
      workId: magicParams.workId,
      useBackendProxy: dxaConfig.useBackendProxy,
      ...magicParams.additionalParams
    };
  } else if (dxaConfig.pegaServerUrl) {
    // Full format - for traditional DXA with Pega server
    console.log('🎯 Using pegaServerUrl DXA format');
    transformedConfig = {
      pegaServerUrl: dxaConfig.pegaServerUrl,
      appAlias: dxaConfig.appAlias,
      clientId: dxaConfig.clientId,
      clientSecret: dxaConfig.clientSecret,
      authFlow: magicParams.authFlow || dxaConfig.authFlow,
      action: magicParams.action,
      pageId: magicParams.pageId,
      pageClass: magicParams.pageClass,
      caseId: magicParams.caseId,
      assignmentId: magicParams.assignmentId,
      workId: magicParams.workId,
      ...magicParams.additionalParams
    };
  } else {
    console.error('❌ Invalid DXA config format. Must have either "baseUrl" or "pegaServerUrl"');
    console.warn('💡 Example with baseUrl:', {
      dxa: {
        enabled: true,
        baseUrl: 'https://your-dxa-app.com',
        defaultAction: 'openPage',
        defaultPageId: 'pyHome'
      }
    });
    console.warn('💡 Example with pegaServerUrl:', {
      dxa: {
        pegaServerUrl: 'https://pega-server.com/prweb',
        appAlias: 'app-alias',
        clientId: 'client-id',
        clientSecret: 'client-secret'
      }
    });
    return null;
  }

  console.log('🔄 Transformed magic URL to DXA config:', transformedConfig);
  return transformedConfig;
}
